import React from 'react';
import { ToggleSettingCard } from '../components/SettingCards/ToggleSetting/ToggleSetting';
import { ToggleSettingGroup } from '../components/SettingCards/ToggleSetting/ToggleSettingGroup';
import { SettingCard } from '../components/SettingCards/SettingCard/SettingCard';
import { RadioSetting } from '../components/SettingCards/RadioSetting/RadioSetting';
import { NavigationSetting } from '../components/SettingCards/NavigationSetting/NavigationSetting';
import {
	getSettingConfig,
	SettingType,
	ToggleSettingConfig,
	ToggleSettingGroupConfig,
	RadioSettingConfig,
	IconRadioSettingConfig,
	NavigationSettingConfig,
	ButtonSettingConfig,
	DropdownSettingConfig,
	RadioSettingGroupConfig,
} from '../config/SettingsDefinitions';

import { ButtonSetting } from '../components/SettingCards/ButtonSetting/ButtonSetting';
import { RadioSettingGroup } from '../components/SettingCards/RadioSetting/RadioSettingGroup';
import {
	DropdownSetting,
	DropdownSettingValue,
} from '../components/SettingCards/DropdownSetting/DropdownSetting';
import { DropdownOption } from '../components/Dropdown/Dropdown';
import { SETTING_NOTE_MESSAGES } from '../components/SettingCards/SettingCard/SettingStatus/SettingNotes';

interface SettingState {
	value: any;
	disabled: boolean;
	initialValue?: any;
}

interface RenderSettingParams {
	settingId: string;
	settingsState: Record<string, SettingState>;
	handleChange?: (settingId: string, value: any) => Promise<void>;
	customComponents?: Record<string, JSX.Element>;
	optionsFilter?: (settingId: string, options: any[]) => any[];
	navigationHandlers?: Record<string, () => void>;
	options?: DropdownOption[];
}

export const renderToggleSetting = ({
	settingId,
	settingsState,
	handleChange,
}: RenderSettingParams): JSX.Element | null => {
	const config = getSettingConfig(settingId);
	const state = settingsState[settingId] || { value: true, disabled: false };

	if (!config || config.type !== SettingType.TOGGLE) return null;

	const toggleConfig = config as ToggleSettingConfig;
	return (
		<ToggleSettingCard
			key={settingId}
			settingId={settingId}
			{...toggleConfig}
			checked={state.value}
			disabled={state.disabled}
			onChange={(checked) => handleChange(settingId, checked)}
		/>
	);
};

export const renderToggleSettingGroup = ({
	settingId,
	settingsState,
	handleChange,
}: RenderSettingParams): JSX.Element | null => {
	const config = getSettingConfig(settingId);
	const state = settingsState[settingId] || { value: true, disabled: false };

	if (!config || config.type !== SettingType.TOGGLE_SETTING_GROUP) return null;

	const groupConfig = config as ToggleSettingGroupConfig;
	const showContentSeparator = groupConfig.showContentSeparator ?? false;

	const groupSettings =
		groupConfig.toggleOptions?.map((subSetting) => {
			const subState = settingsState[subSetting.id] || { value: true, disabled: false };
			const subValue = subState.value ?? true;
			const subDisabled = state.disabled || (subState.disabled ?? false);
			return {
				settingId: subSetting.id,
				title: subSetting.title,
				checked: subValue,
				disabled: subDisabled,
				onChange: (checked: boolean) => handleChange(subSetting.id, checked),
			};
		}) || [];

	const anySubSettingDisabled = groupSettings.some((s) => s.disabled);
	const showAdminNote = anySubSettingDisabled && !state.disabled;

	return (
		<SettingCard
			key={settingId}
			settingId={settingId}
			title={groupConfig.title}
			description={groupConfig.description}
			disabled={state.disabled}
			showAdminNote={showAdminNote}
			content={<ToggleSettingGroup settings={groupSettings} />}
			showContentSeparator={showContentSeparator}
		/>
	);
};

export const renderRadioSettingGroup = ({
	settingId,
	settingsState,
	handleChange,
}: RenderSettingParams): JSX.Element | null => {
	const config = getSettingConfig(settingId);
	const state = settingsState[settingId] || { value: true, disabled: false };

	if (!config || config.type !== SettingType.RADIO_SETTING_GROUP) return null;

	const groupConfig = config as RadioSettingGroupConfig;
	const showContentSeparator = groupConfig.showContentSeparator ?? false;

	const groupSettings =
		groupConfig.radioOptions?.map((subSetting) => {
			const subState = settingsState[subSetting.id] || { value: '', disabled: false };
			const subValue = subState.value || subSetting.options[0]?.value || '';
			const subDisabled = state.disabled || (subState.disabled ?? false);
			return {
				settingId: subSetting.id,
				title: subSetting.title,
				description: subSetting.description,
				options: subSetting.options,
				selectedValue: subValue,
				disabled: subDisabled,
				onChange: (value: string) => handleChange(subSetting.id, value),
			};
		}) || [];

	const anySubSettingDisabled = groupSettings.some((s) => s.disabled);
	const showAdminNote = anySubSettingDisabled && !state.disabled;

	return (
		<SettingCard
			key={settingId}
			settingId={settingId}
			title={groupConfig.title}
			description={groupConfig.description}
			disabled={state.disabled}
			showAdminNote={showAdminNote}
			content={<RadioSettingGroup settings={groupSettings} />}
			showContentSeparator={showContentSeparator}
		/>
	);
};

export const renderRadioSetting = ({
	settingId,
	settingsState,
	handleChange,
	optionsFilter,
}: RenderSettingParams): JSX.Element | null => {
	const config = getSettingConfig(settingId);
	const state = settingsState[settingId] || { value: '', disabled: false };

	if (
		!config ||
		(config.type !== SettingType.RADIO && config.type !== SettingType.ICON_RADIO)
	)
		return null;

	let radioConfig = config as RadioSettingConfig | IconRadioSettingConfig;

	if (optionsFilter && radioConfig.options) {
		radioConfig = {
			...radioConfig,
			options: optionsFilter(settingId, radioConfig.options),
		};
	}

	return (
		<RadioSetting
			key={settingId}
			settingId={settingId}
			{...radioConfig}
			selectedValue={state.value}
			onChange={(value) => handleChange(settingId, value)}
			showStatusOnValueChange={true}
			statusMessage={SETTING_NOTE_MESSAGES.RESTART_ACTIVE_SESSIONS}
			disabled={state.disabled}
		/>
	);
};

export const renderNavigationSetting = ({
	settingId,
	navigationHandlers,
}: {
	settingId: string;
	navigationHandlers?: Record<string, () => void>;
}): JSX.Element | null => {
	const config = getSettingConfig(settingId);

	if (!config || config.type !== SettingType.NAVIGATION) return null;

	const navConfig = config as NavigationSettingConfig;
	const onClick = navigationHandlers?.[settingId];

	return (
		<NavigationSetting
			key={settingId}
			settingId={settingId}
			{...navConfig}
			onClick={onClick}
		/>
	);
};

/**
 * Universal setting renderer that handles all setting types
 * @param params - Parameters for rendering any setting type
 * @returns JSX element or null if config not found
 */
export const SettingsRenderer = (
	renderSettingParams: RenderSettingParams
): JSX.Element | null => {
	if (
		renderSettingParams.customComponents &&
		renderSettingParams.customComponents[renderSettingParams.settingId]
	) {
		return renderSettingParams.customComponents[renderSettingParams.settingId];
	}

	const config = getSettingConfig(renderSettingParams.settingId);
	if (!config) return null;

	switch (config.type) {
		case SettingType.TOGGLE:
			return renderToggleSetting({
				settingId: renderSettingParams.settingId,
				settingsState: renderSettingParams.settingsState,
				handleChange: renderSettingParams.handleChange,
			});

		case SettingType.TOGGLE_SETTING_GROUP:
			return renderToggleSettingGroup({
				settingId: renderSettingParams.settingId,
				settingsState: renderSettingParams.settingsState,
				handleChange: renderSettingParams.handleChange,
			});

		case SettingType.RADIO_SETTING_GROUP:
			return renderRadioSettingGroup({
				settingId: renderSettingParams.settingId,
				settingsState: renderSettingParams.settingsState,
				handleChange: renderSettingParams.handleChange,
			});

		case SettingType.RADIO:
		case SettingType.ICON_RADIO:
			if (!renderSettingParams.handleChange) return null;

			return renderRadioSetting({
				settingId: renderSettingParams.settingId,
				settingsState: renderSettingParams.settingsState,
				handleChange: renderSettingParams.handleChange,
				optionsFilter: renderSettingParams.optionsFilter,
			});

		case SettingType.NAVIGATION:
			return renderNavigationSetting({
				settingId: renderSettingParams.settingId,
				navigationHandlers: renderSettingParams.navigationHandlers,
			});

		case SettingType.BUTTON: {
			// For button settings, wire up the onClick handler for each button
			const buttonConfig = config as ButtonSettingConfig;
			const buttons =
				buttonConfig.buttons?.map((btn) => ({
					...btn,
					onClick: async () => {
						if (renderSettingParams.handleChange) {
							await renderSettingParams.handleChange(renderSettingParams.settingId, true);
						}
					},
					disabled:
						renderSettingParams.settingsState[renderSettingParams.settingId]?.disabled,
				})) || [];
			return (
				<ButtonSetting
					key={renderSettingParams.settingId}
					title={buttonConfig.title}
					description={buttonConfig.description}
					buttons={buttons}
					settingId={renderSettingParams.settingId}
					disabled={
						renderSettingParams.settingsState[renderSettingParams.settingId]?.disabled
					}
				/>
			);
		}

		case SettingType.DROPDOWN:
			const dropdownState =
				renderSettingParams.settingsState[renderSettingParams.settingId];

			let selectedValue = '';
			let options: Array<{ value: string; label: string }> = [];
			const dropdownConfig = getSettingConfig(
				renderSettingParams.settingId
			) as DropdownSettingConfig;

			if (!dropdownConfig) return null;

			// Handle different value formats
			if (dropdownState && typeof dropdownState.value === 'string') {
				try {
					const parsedValue: DropdownSettingValue = JSON.parse(dropdownState.value);
					selectedValue = parsedValue.selectedOption || '';
					options = parsedValue.availableOptions || dropdownConfig.options || [];
				} catch (e) {
					// If parsing fails, use the string as selectedValue and config options
					selectedValue = dropdownState.value;
					options = dropdownConfig.options || [];
				}
			} else if (
				dropdownState &&
				dropdownState.value &&
				typeof dropdownState.value === 'object'
			) {
				const parsedValue = dropdownState.value as DropdownSettingValue;
				selectedValue = parsedValue.selectedOption || '';
				options = parsedValue.availableOptions || dropdownConfig.options || [];
			} else {
				options = dropdownConfig.options || [];
			}

			return (
				<DropdownSetting
					key={renderSettingParams.settingId}
					title={dropdownConfig.title}
					description={dropdownConfig.description}
					label={dropdownConfig.label}
					settingId={renderSettingParams.settingId}
					options={options}
					selectedValue={selectedValue}
					disabled={dropdownState?.disabled || false}
					learnMoreUrl={dropdownConfig.learnMoreUrl}
					learnMoreText={dropdownConfig.learnMoreText}
					onChange={(value: string) =>
						renderSettingParams.handleChange?.(renderSettingParams.settingId, value)
					}
				/>
			);
		default:
			return null;
	}
};
